if (!customElements.get('bought-together-recommendations')) {
  customElements.define(
    'bought-together-recommendations',
    class BoughtTogetherRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.dataset.recommendationsLoaded === 'true') return;
        this.dataset.recommendationsLoaded = 'true';

        const { url, productId, sectionId, blockId } = this.dataset;
        if (!url || !productId || !sectionId || !blockId) return;

        fetch(`${url}&product_id=${productId}&section_id=${sectionId}`)
          .then((response) => {
            if (!response.ok) throw new Error(`Complementary recommendations request failed: ${response.status}`);
            return response.text();
          })
          .then((text) => {
            const responseDocument = new DOMParser().parseFromString(text, 'text/html');
            const selector = `bought-together-recommendations[data-block-id="${CSS.escape(blockId)}"]`;
            const recommendations = responseDocument.querySelector(selector);

            if (recommendations?.innerHTML.trim()) {
              this.innerHTML = recommendations.innerHTML;
            }
          })
          .catch((error) => console.error(error));
      }
    }
  );
}

if (!customElements.get('bought-together-cart')) {
  customElements.define(
    'bought-together-cart',
    class BoughtTogetherCart extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[data-fbt-submit]');
        this.submitText = this.querySelector('[data-fbt-submit-text]') || this.submitButton?.querySelector('span');
        this.status = this.querySelector('[data-fbt-status]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.currencySymbol = this.dataset.currencySymbol || '₹';
        this.baseButtonLabel = this.dataset.buttonLabel || this.submitText?.textContent?.trim() || 'Add selected to cart';

        if (!this.form || !this.submitButton) return;
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.form.addEventListener('change', this.updateButtonState.bind(this));

        this.initSlider();
        this.updateButtonState();
      }

      initSlider() {
        this.slider = this.querySelector('[data-fbt-slider]');
        const parentContainer = this.closest('.prithviya-fbt__inner') || this;
        this.prevBtn = parentContainer.querySelector('[data-fbt-prev]');
        this.nextBtn = parentContainer.querySelector('[data-fbt-next]');
        this.navWrapper = parentContainer.querySelector('[data-fbt-nav]');

        if (!this.slider) return;

        const updateNav = () => {
          const hasOverflow = this.slider.scrollHeight > (this.slider.clientHeight + 4);
          if (this.navWrapper) {
            this.navWrapper.style.display = hasOverflow ? 'flex' : 'none';
          }
          if (this.prevBtn) {
            this.prevBtn.disabled = this.slider.scrollTop <= 4;
          }
          if (this.nextBtn) {
            this.nextBtn.disabled = Math.ceil(this.slider.scrollTop + this.slider.clientHeight) >= (this.slider.scrollHeight - 4);
          }
        };

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const row = this.slider.querySelector('[data-fbt-row]');
            const step = row ? (row.offsetHeight + 12) : 88;
            this.slider.scrollBy({ top: -step, behavior: 'smooth' });
          });
        }

        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const row = this.slider.querySelector('[data-fbt-row]');
            const step = row ? (row.offsetHeight + 12) : 88;
            this.slider.scrollBy({ top: step, behavior: 'smooth' });
          });
        }

        this.slider.addEventListener('scroll', updateNav, { passive: true });

        // Update when images load or layout shifts
        requestAnimationFrame(updateNav);
        setTimeout(updateNav, 300);
        window.addEventListener('resize', updateNav, { passive: true });
      }

      getSelectedItems() {
        return Array.from(this.querySelectorAll('[data-fbt-variant]'))
          .filter((input) => input.checked && !input.disabled)
          .map((input) => ({ id: Number(input.value), quantity: 1 }))
          .filter((item) => Number.isInteger(item.id) && item.id > 0);
      }

      updateButtonState() {
        if (!this.submitButton) return;
        const selected = this.getSelectedItems();
        this.submitButton.disabled = selected.length === 0;

        let totalCents = 0;
        const selectedInputs = Array.from(this.querySelectorAll('[data-fbt-variant]')).filter(
          (input) => input.checked && !input.disabled
        );

        selectedInputs.forEach((input) => {
          const price = parseInt(input.dataset.price, 10);
          if (!isNaN(price) && price > 0) {
            totalCents += price;
          }
        });

        const selectedCount = this.querySelector('[data-fbt-selected-count]');
        if (selectedCount) selectedCount.textContent = selectedInputs.length;

        this.querySelectorAll('[data-fbt-row]').forEach((row) => {
          const input = row.querySelector('[data-fbt-variant]');
          row.classList.toggle('is-selected', Boolean(input?.checked && !input.disabled));
        });

        if (this.submitText) {
          if (totalCents > 0) {
            const rupees = Math.round(totalCents / 100);
            this.submitText.textContent = `${this.baseButtonLabel} • ${this.currencySymbol} ${rupees.toLocaleString('en-IN')}`;
          } else {
            this.submitText.textContent = this.baseButtonLabel;
          }
        }
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.submitButton?.disabled) return;

        this.setLoading(true);
        this.setStatus('');

        try {
          await this.addSelectedItems();
        } catch (error) {
          this.setStatus(error.message || window.cartStrings?.error || 'Unable to add selected products.');
        } finally {
          this.setLoading(false);
        }
      }

      async addSelectedItems() {
        const items = this.getSelectedItems();
        if (items.length === 0) return;

        const config = fetchConfig('javascript');
        config.headers['Content-Type'] = 'application/json';

        const payload = { items };
        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
          payload.sections_url = window.location.pathname;
          this.cart.setActiveElement?.(this.submitButton);
        }
        config.body = JSON.stringify(payload);

        const request = await fetch(routes.cart_add_url, config);
        const response = await request.json();
        if (!request.ok || response.status) {
          throw new Error(response.description || response.message || window.cartStrings?.error || 'Unable to add selected products.');
        }

        if (!this.cart) {
          window.location = routes.cart_url;
          return;
        }

        this.cart.classList?.remove('is-empty');
        this.cart.renderContents(response);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bought-together-cart', cartData: response });
        }
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.classList.toggle('loading', isLoading);
        this.submitButton.toggleAttribute('aria-disabled', isLoading);
        this.querySelector('[data-fbt-spinner]')?.classList.toggle('hidden', !isLoading);
      }

      setStatus(message) {
        if (!this.status) return;
        this.status.textContent = message;
        this.status.toggleAttribute('hidden', !message);
      }
    }
  );
}
